import {
  Annotation,
  PageLabel,
  PageLabelList,
  RCSBookData,
} from '@readcloud/data';
import { decodeJwt } from '@readcloud/common';
import { EpubCFI } from 'epubjs';
import Locations from 'epubjs/types/locations';
import { uniq } from 'ramda';
import ReactPlayer from 'react-player';
import { ProductRights, ProductTokenPayload } from './runtimeTypes';

export type Type = 'PDF' | 'EPUB' | 'PLAYER' | 'IMAGE' | 'UNKNOWN';
export const getBookTypeFromMime = (mime: string): Type =>
  mime === 'application/pdf'
    ? 'PDF'
    : mime === 'application/epub+zip'
    ? 'EPUB'
    : 'UNKNOWN';
export const getType = (url: string): Type =>
  /\.epub/.test(url)
    ? 'EPUB'
    : /\.pdf/.test(url)
    ? 'PDF'
    : /\.(gif|jpg|jpeg|png|tiff|svg)/.test(url)
    ? 'IMAGE'
    : ReactPlayer.canPlay(url)
    ? 'PLAYER'
    : 'UNKNOWN';

export const makePageLabelsByLocation = (pageLabels: PageLabelList) =>
  pageLabels.reduce(
    (acc, pageLabel) => ({ ...acc, [pageLabel.value]: pageLabel }),
    {}
  );

export const getPageLabelByString = (
  location: string,
  locations?: Locations,
  pageLabelsByLocation?: { [k: string]: PageLabel }
) => {
  const epubCfi = new EpubCFI();
  return `${
    pageLabelsByLocation && pageLabelsByLocation[location]
      ? pageLabelsByLocation[location].label
      : epubCfi && epubCfi.isCfiString(location) && locations
      ? locations.locationFromCfi(location).toString()
      : location
  }`;
};

export const getPageLabelByAnnotation = (
  fileUrl: string,
  location: Annotation['location'],
  locations?: Locations,
  pageLabels?: PageLabelList
) => {
  const epubCfi = new EpubCFI();
  return `${
    getType(fileUrl) === 'PDF' && 'pageNumber' in location
      ? pageLabels && pageLabels.length && pageLabels[location.pageNumber - 1]
        ? pageLabels[location.pageNumber - 1].label
        : location.pageNumber
      : epubCfi &&
        'range' in location &&
        location.range &&
        epubCfi.isCfiString(location.range) &&
        locations
      ? locations.locationFromCfi(location.range)
      : ''
  }`;
};

export const getPrintablePageListFromRanges = (printableRanges?: string) =>
  !printableRanges
    ? []
    : uniq(
        printableRanges
          .split(',')
          .map((s) => s.trim())
          .filter((s) => !!s.length)
          .flatMap((s) => {
            const range = s.split('-');
            if (range.length === 1) {
              const page = parseInt(range[0], 10);
              if (!isNaN(page)) {
                return [page];
              } else {
                return [];
              }
            } else if (range.length === 2) {
              const start = parseInt(range[0], 10);
              const end = parseInt(range[1], 10);
              if (!isNaN(start) && !isNaN(end)) {
                return Array.from(
                  Array(Math.abs(end - start) + 1),
                  (_, i) => i + Math.min(start, end)
                );
              } else {
                return [];
              }
            } else {
              return [];
            }
          })
      );

export const getPrintConfigFromRcsData = (rcsData?: RCSBookData) => {
  if (rcsData && rcsData.licenceToken && rcsData.productToken) {
    const { payload: productTokenPayload } = decodeJwt(rcsData.productToken);
    if (ProductTokenPayload.is(productTokenPayload)) {
      const parsedRights = () => {
        try {
          return JSON.parse(productTokenPayload['rights.json']);
        } catch {
          return 'Parse Error';
        }
      };

      if (ProductRights.is(parsedRights())) {
        if (parsedRights().printPages && parsedRights().printPages.length) {
          const printablePages = getPrintablePageListFromRanges(
            parsedRights().printPages
          );
          return printablePages.length
            ? {
                printableRanges: parsedRights().printPages,
                printablePages,
              }
            : undefined;
        }
      }
    }
  }

  return;
};
